import './CourseCarousel.scss';
import * as React from 'react';
import { ITagProps } from '../Tag';
import Tag from '../Tag';
import Icon from '../Icon';
import NavButton from './NavButton';
import CircularProgress, { ProgressTimer } from './CircularProgress';
import CarouselImage from './Image';
import CarouselLocation from './Location';
import getActivityStatus from 'src/utils/activityStatusUtils';
import Tags from '../Tag/Tags';

export interface ICourseCarouselItem {
  status: ReturnType<typeof getActivityStatus>;
  id: string;
  title: string;
  description: string;
  location: string;
  startDateTime: string | null;
  endDateTime: string | null;
  themeImageUrl: string | null;
  theme: ITagProps;
  tag: ITagProps;
  portalPageUrl?: string;
  admStatus: string;
  signUpForm: string;
  isAdvertisement?: boolean;
}

export interface ICourseCarouselProps {
  courses: ICourseCarouselItem[];
  className?: string;
  onActivitySelect: (courseId: string) => void;
}

const CourseCarousel = ({
  courses,
  className,
  onActivitySelect
}: ICourseCarouselProps) => {
  const innerCloneRef = React.useRef<HTMLDivElement | null>(null);
  const innerRef = React.useRef<HTMLDivElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);

  const [currentIndex, setCurrentIndex] = React.useState(0);
  const currentCourse = React.useMemo(() => {
    return courses[currentIndex];
  }, [currentIndex, courses]);

  const setNextIndex = React.useCallback((nextIndex: number) => {
    if (innerRef.current && contentRef.current) {
      innerRef.current.classList.remove('carousel-in');

      const inner = innerRef.current;
      const innerRect = inner.getBoundingClientRect();
      const clone = inner.cloneNode(true) as HTMLElement;

      clone.style.position = 'absolute';
      clone.style.height = `${innerRect.height}px`;
      clone.style.width = `${innerRect.width}px`;
      clone.classList.add('carousel-out');

      clone.addEventListener('animationend', () => {
        clone.remove();
      });

      contentRef.current.appendChild(clone);
    }

    if (nextIndex < 0) {
      return setCurrentIndex(courses.length - 1);
    } else if (nextIndex >= courses.length) {
      return setCurrentIndex(0);
    }

    setCurrentIndex(nextIndex);
  }, [courses, currentIndex]);

  React.useLayoutEffect(() => {
    if (!innerRef.current) {
      return;
    }

    const inner = innerRef.current;
    inner.classList.add('carousel-in');
    inner.addEventListener('animationend', () => {
      inner.classList.remove('carousel-in');
    });
  }, [currentCourse]);

  const timer = React.useRef<ProgressTimer>(null);

  if (!currentCourse) {
    return null;
  }

  return (
    <div
      className="course-carousel"
      onMouseEnter={() => timer.current?.pause()}
      onMouseLeave={() => timer.current?.start()}
    >
      <nav className="course-carousel-nav">
        <NavButton
          onClick={() => {
            setNextIndex(currentIndex - 1);
            timer.current?.reset();
          }}
          type="back"
        />
        <CircularProgress
          onTime={() => setNextIndex(currentIndex + 1)}
          duration={10}
          ref={timer}
        />
        <NavButton
          onClick={() => {
            setNextIndex(currentIndex + 1);
            timer.current?.reset();
          }}
          type="forward"
        />
      </nav>
      <div className="course-carousel-content" ref={contentRef}>
        <div ref={innerRef}>
          <Tags className="pb-2">
            <Tag {...currentCourse.theme} color="white" size="small" />
            <Tag color="dark-blue" size="small">FREMHÆVET KURSUS</Tag>
          </Tags>
          <h2>{currentCourse.title}</h2>
          <p>{currentCourse.description.slice(0, 250)} </p>
          <div className="flex items-center mt4">
            {currentCourse.signUpForm !== 'A' && (
              <button
                className="course-carousel-cta"
                onClick={() => onActivitySelect(currentCourse.id)}
              >
                Ansøg
                <Icon name="chevron-right" />
              </button>
            )}
            <button
              className="course-carousel-read-more"
              onClick={() => onActivitySelect(currentCourse.id)}
            >
              <span>Læs mere</span>
            </button>
          </div>
        </div>
        <CarouselLocation
          key={currentCourse.id}
          status={currentCourse.status}
          location={currentCourse.location}
          dateRange={currentCourse.startDateTime && currentCourse.endDateTime
            ? [currentCourse.startDateTime, currentCourse.endDateTime]
            : null
          }
        />
      </div>
      <CarouselImage
        src={currentCourse.themeImageUrl}
      />
    </div>
  );
};

export default CourseCarousel;



// WEBPACK FOOTER //
// ./src/components/CourseCarousel/index.tsx